/**
 * Central site-origin config — single source of truth for the deployed host.
 *
 * The app is served box-direct from the ECS host (currently 47.108.215.19).
 * Everything is same-origin: the browser hits `/api` and the box's nginx
 * proxies it to mgv-api on :3000, so no separate api/www host is needed.
 *
 * Override at build time with:
 *   REACT_APP_SITE_ORIGIN  — absolute origin for absolute/shared URLs
 *                            (emails, wiki viewer links, OSS/CDN refs)
 *   REACT_APP_API_URL      — browser API prefix (already ends in /api);
 *                            defaults to same-origin '/api'
 *   REACT_APP_WIKI_BASE    — wiki API base; defaults to SITE_ORIGIN/api/wiki
 */

// Default deployment origin for THIS app (standalone Language Center).
// The upstream monolith pointed this at the shared ECS box IP; this repo is a
// separate product served from its own hostname. Override at build time with
// REACT_APP_SITE_ORIGIN if the canonical host changes.
const DEFAULT_SITE_ORIGIN = 'https://omniversail.com';

function stripTrailingSlash(s) {
  return String(s || '').replace(/\/+$/, '');
}

export const SITE_ORIGIN =
  stripTrailingSlash(process.env.REACT_APP_SITE_ORIGIN || DEFAULT_SITE_ORIGIN) || DEFAULT_SITE_ORIGIN;

// Browser API prefix (ends in /api). Same-origin by default.
export const API_URL = stripTrailingSlash(process.env.REACT_APP_API_URL || '/api') || '/api';

// Wiki API base.
export const WIKI_BASE =
  stripTrailingSlash(process.env.REACT_APP_WIKI_BASE || `${SITE_ORIGIN}/api/wiki`) ||
  `${SITE_ORIGIN}/api/wiki`;

// Absolute wiki mirror viewer (Civ VI, etc.).
export const CIV_WIKI_MIRROR = `${SITE_ORIGIN}/api/wiki/civ/mirror/viewer/index.html`;

// Absolute original-book PDF (fair-quality reference scan) for Civ VI.
export const CIV_WIKI_PDF = `${SITE_ORIGIN}/api/wiki/civ/pdf/civilization-vi-reference.pdf`;

// Absolute backend origin for node-side scripts / emails (no /api suffix).
export const SITE_BACKEND_ORIGIN = SITE_ORIGIN;

// ── Branding / contact (consumed by the shared Footer component) ──────────────
export const SITE_NAME = process.env.REACT_APP_SITE_NAME || 'Omniversail Language Center';
export const CONTACT_EMAIL = process.env.REACT_APP_CONTACT_EMAIL || 'contact@omniversail.com';
export const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || 'support@omniversail.com';
export const CAREERS_EMAIL = process.env.REACT_APP_CAREERS_EMAIL || 'careers@omniversail.com';

// Footer "Questions? Email us" — every address is rendered as a clickable
// mailto: link (the footer reveals them on demand, see components/Footer.js).
export const FOOTER_EMAILS = [
  { id: 'general', label: 'General questions', email: CONTACT_EMAIL },
  { id: 'support', label: 'Support', email: SUPPORT_EMAIL },
  { id: 'careers', label: 'Careers', email: CAREERS_EMAIL },
].filter((e) => e.email);

// Social links shown in the footer (env-driven; empty entries are filtered out).
export const SOCIAL_LINKS = [
  { label: 'YouTube', href: process.env.REACT_APP_SOCIAL_YOUTUBE || '' },
  { label: 'Instagram', href: process.env.REACT_APP_SOCIAL_INSTAGRAM || '' },
  { label: 'X', href: process.env.REACT_APP_SOCIAL_X || '' },
  { label: 'WeChat', href: process.env.REACT_APP_SOCIAL_WECHAT || '' },
].filter((s) => s.href);

// Footer link columns — kept to routes that actually exist in this fork.
// `name` doubles as the English fallback for the i18n lookup (ov.footer.links.*).
export const FOOTER_LINKS = {
  services: [
    { id: 'language', name: 'Language Learning', path: '/language' },
    { id: 'curriculum', name: 'Curriculum', path: '/curriculum' },
    { id: 'prep', name: 'Prep', path: '/curriculum?track=prep' },
    { id: 'colleges', name: 'Colleges', path: '/colleges' },
  ],
  pricing: [
    { id: 'pricingPlans', name: 'View Plans', path: '/#pricing' },
    // plan ids are the catalog ids from config/plans.js (free / self / intensive);
    // the old ?plan=monthly|annual values fell back to the default plan silently.
    { id: 'pricingMonthly', name: 'Monthly Plan', path: '/checkout?plan=self&interval=monthly' },
    { id: 'pricingAnnual', name: 'Annual Plan', path: '/checkout?plan=self&interval=yearly' },
  ],
  company: [
    { id: 'about', name: 'About', path: '/about' },
    { id: 'contact', name: 'Contact', path: '/contact' },
    { id: 'careers', name: 'Careers', path: '/careers' },
  ],
};

export default {
  SITE_ORIGIN,
  API_URL,
  WIKI_BASE,
  CIV_WIKI_MIRROR,
  CIV_WIKI_PDF,
  SITE_BACKEND_ORIGIN,
  SITE_NAME,
  CONTACT_EMAIL,
  SUPPORT_EMAIL,
  CAREERS_EMAIL,
  FOOTER_EMAILS,
  SOCIAL_LINKS,
  FOOTER_LINKS,
};
